import React, { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Download, GraduationCap, Plus, Search, Star, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DemoForm } from "./DemoForm";
import { DemoDetail, TONE } from "./DemoDetail";
import { DemoExpenseReport } from "./DemoExpenseReport";
import { DemoCalendar } from "./DemoCalendar";

export default function Demos() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [tab, setTab] = useState("list");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/demos", { params: { q, status, kind } });
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, status, kind]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const open = rows.filter((r) => ["Proposed", "Confirmed", "Rescheduled"].includes(r.status));
  const done = rows.filter((r) => ["Completed", "Feedback Received"].includes(r.status));
  const rated = rows.filter((r) => r.feedback?.rating);
  const avg = rated.length
    ? +(rated.reduce((a, r) => a + r.feedback.rating, 0) / rated.length).toFixed(1) : 0;
  const spend = rows.reduce((a, r) => a + Number(r.expense_total || 0), 0);

  const columns = [
    {
      key: "customer", label: "Customer",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{r.customer_name}</p>
          <p className="num truncate text-xs text-muted-foreground">
            {r.number} &middot; {r.kind}
            {r.lead_id ? <span className="ml-1 text-primary">from CRM lead</span> : null}
          </p>
        </div>
      ),
    },
    {
      key: "dates", label: "Dates",
      render: (r) => (
        <span className="text-sm">
          {r.start_date ? `${fdate(r.start_date)} \u2013 ${fdate(r.end_date)}`
            : <span className="text-muted-foreground">proposed {fdate(r.proposed_start)}</span>}
        </span>
      ),
    },
    { key: "team", label: "Team", render: (r) => <span className="text-sm">{(r.assigned_to_names || []).join(", ") || "\u2014"}</span>, hideMobile: true },
    { key: "location", label: "Venue", render: (r) => <span className="text-sm text-muted-foreground">{r.mode}{r.location ? ` \u00b7 ${r.location}` : ""}</span>, hideMobile: true },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={TONE[r.status]} /> },
    {
      key: "rating", label: "Rating", align: "right",
      render: (r) => r.feedback?.rating
        ? <span className="num inline-flex items-center gap-1 text-sm font-bold">
            {r.feedback.rating}<Star className="h-3.5 w-3.5 fill-primary text-primary" />
          </span>
        : <span className="text-xs text-muted-foreground">{r.outcome || "\u2014"}</span>,
    },
    { key: "spend", label: "Expenses", align: "right", render: (r) => <span className="num text-sm">{money(r.expense_total || 0)}</span>, hideMobile: true },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={GraduationCap}
        title="Demos &amp; Training"
        subtitle="Schedule a visit, keep the customer informed at every change, and see what each session cost and earned."
        actions={
          <div className="flex flex-wrap gap-2">
            {rows.length > 0 && can("demos", "export") && (
              <Button variant="outline" onClick={() => {
                downloadCSV(rows.map((r) => ({
                  Reference: r.number, Type: r.kind, Customer: r.customer_name, Company: r.company_name,
                  Status: r.status, From: r.start_date || r.proposed_start, To: r.end_date || r.proposed_end,
                  Days: r.actual_days || r.days, Team: (r.assigned_to_names || []).join(" / "),
                  Performance: r.performance, Outcome: r.outcome, Rating: r.feedback?.rating || "",
                  Expenses: r.expense_total || 0,
                })), "demos.csv");
                toast.success("Exported");
              }} data-testid="demo-export-btn">
                <Download className="h-4 w-4" /> Export
              </Button>
            )}
            {can("demos", "add") && (
              <Button onClick={() => setFormOpen(true)} data-testid="new-demo-btn">
                <Plus className="h-4 w-4" /> Schedule demo / training
              </Button>
            )}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="demo-kpis">
        <StatCard label="Upcoming" value={open.length} icon="CalendarCheck" tone="info" testId="kpi-upcoming" />
        <StatCard label="Completed" value={done.length} icon="GraduationCap" tone="success" testId="kpi-completed" />
        <StatCard label="Average rating" value={avg} icon="Star" tone="primary" testId="kpi-avg-rating"
          hint={rated.length ? `${rated.length} session(s) rated out of 5` : "no ratings yet"} />
        <StatCard label="Visit expenses" value={spend} currency icon="Wallet" tone="warning" testId="kpi-visit-expenses"
          hint="claimed across all visits" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" data-testid="demo-tab-list">Schedule</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="demo-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="demo-tab-expenses">Expense report</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-5 space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="demo-search"
            placeholder="Search by customer, reference or venue" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[190px]" data-testid="demo-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Upcoming only</SelectItem>
            {["Proposed", "Confirmed", "Rescheduled", "Completed", "Feedback Received", "Cancelled"]
              .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-[150px]" data-testid="demo-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Demo &amp; training</SelectItem>
            <SelectItem value="Demo">Demo only</SelectItem>
            <SelectItem value="Training">Training only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingRows /> : (
        <DataTable columns={columns} rows={rows} onRowClick={(r) => setDetailId(r.id)}
          rowTestId={(r) => `demo-row-${r.number}`}
          rowClassName={(r) => r.status === "Cancelled" && "opacity-60"}
          empty={<EmptyState icon={GraduationCap} title="No demos or trainings yet"
            description="Schedule one and the customer is emailed the proposed dates straight away."
            action={can("demos", "add") && <Button data-testid="demo-empty-new-btn" onClick={() => setFormOpen(true)}><Plus className="h-4 w-4" /> Schedule one</Button>} />} />
      )}
        </TabsContent>

        <TabsContent value="calendar" className="mt-5">
          <DemoCalendar onOpen={(id) => setDetailId(id)} />
        </TabsContent>

        <TabsContent value="expenses" className="mt-5">
          <DemoExpenseReport />
        </TabsContent>
      </Tabs>

      <DemoForm open={formOpen} onOpenChange={setFormOpen} onSaved={load} />
      <DemoDetail demoId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load} />
    </div>
  );
}
